/**
 * How every app names a browser tab, held once: the page's own name, then the product's. An app's root layout hands
 * Next `productTitleTemplate(<product>)` as its `title.template`, and Next fills it for every route BELOW the layout's
 * folder. It never fills it for the page in the layout's OWN folder (`app/page.tsx` under the root layout; seen on
 * dev.federatedholdings.com 2026-09-28, where the home tab read "General ledger" alone), so that page names its tab
 * through `titleInTemplate`. `QUAL-NEXT-PAGE-TITLE-OUTSIDE-OWN-TEMPLATE-001` refuses a bare title there, and
 * `QUAL-PAGE-TITLE-TEMPLATE-LITERAL-SSOT-001` refuses the grammar spelled anywhere but here.
 */

import type { AbsolutePageTitle } from '@federa/types/page-title';

/** The `title.template` a root layout declares: `%s` is the slot Next fills with a page's own title.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): the one spelling of the tab-title grammar every root layout hands Next. */
export function productTitleTemplate(product: string): string {
	return `%s | ${product}`;
}

/** A page's own title filled into its layout's template, for the page Next leaves outside that template. The replacer
 *  is a function, so a `$` in the page's name stays text rather than a replacement pattern.
 *  @runtime-derived-ssot-helper: fills the same-file grammar's `%s` slot, so no page re-spells the template. */
export function titleInTemplate(template: string, pageTitle: string): AbsolutePageTitle {
	return { absolute: template.replace('%s', () => pageTitle) };
}
